import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, Trash2, ShoppingCart, ShieldCheck, CheckCircle2, CreditCard, Banknote, MessageCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API, waLink } from "../config";
import { meta, cartParams } from "../lib/metaPixel";
import { useCart } from "../cart/CartContext";
import { Seo } from "../components/Seo";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

const inputCls =
  "w-full rounded-xl border border-ink/15 bg-white px-5 py-4 text-sm text-ink placeholder:text-ink/35 outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-colors duration-300";

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function CartPage() {
  const { items, setQty, remove, clear, total } = useCart();
  const [form, setForm] = useState({ name: "", phone: "", email: "", address1: "", landmark: "", city: "", state: "", pincode: "" });
  const [method, setMethod] = useState("online");
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState(null);

  const [coupon, setCoupon] = useState(() => localStorage.getItem("phudg_coupon") || "");
  const [couponApplied, setCouponApplied] = useState(() => localStorage.getItem("phudg_coupon") || "");
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    if (!items.length) {
      setQuote(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API}/offers/quote`, {
          items: items.map((i) => ({ slug: i.slug, size: i.size, qty: i.qty })),
          coupon_code: couponApplied,
        });
        setQuote(data);
      } catch {
        setQuote(null);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [items, couponApplied]);

  const payable = (method === "online" ? quote?.prepaid_total : quote?.total) ?? total;

  const applyCoupon = () => {
    const code = coupon.trim().toUpperCase();
    if (!code) return;
    setCouponApplied(code);
  };

  const litreCount = items.filter((i) => /^1\s*l/i.test(i.size.trim())).reduce((n, i) => n + i.qty, 0);

  useEffect(() => {
    if (placed) {
      if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [placed]);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const fullAddress = () =>
    [form.address1, form.landmark, form.city, form.state, form.pincode].map((s) => s.trim()).filter(Boolean).join(", ");

  const validate = () => {
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim() || !form.address1.trim() || !form.city.trim() || !form.state.trim() || !form.pincode.trim()) {
      toast.error("Please fill in all required delivery details first.");
      return false;
    }
    if (!/^\d{10}$/.test(form.phone.replace(/[\s+\-]/g, "").replace(/^91/, ""))) {
      toast.error("Please enter a valid 10-digit phone number.");
      return false;
    }
    if (!/^\d{6}$/.test(form.pincode.trim())) {
      toast.error("Please enter a valid 6-digit PIN code.");
      return false;
    }
    return true;
  };

  const orderPayload = () => ({
    items: items.map((i) => ({ slug: i.slug, size: i.size, qty: i.qty })),
    customer: form,
    coupon_code: quote?.coupon_discount > 0 ? couponApplied : "",
  });

  const payOnline = async () => {
    if (!validate()) return;
    setPlacing(true);
    try {
      const loaded = await loadRazorpay();
      if (!loaded) throw new Error("checkout load failed");
      const { data } = await axios.post(`${API}/payments/create-order`, orderPayload());
      const purchasedItems = items.map((i) => ({ ...i }));
      const orderValue = payable;
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: "INR",
        order_id: data.order_id,
        name: "PhudG",
        description: "Cold-Pressed Oils",
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: "#556B2F" },
        handler: async (resp) => {
          try {
            await axios.post(`${API}/payments/verify`, resp);
            meta.purchase(cartParams(purchasedItems, orderValue), data.order_id);
            clear();
            setPlaced({ id: data.order_id, mode: "online" });
            toast.success("Payment received — thank you for choosing PhudG!");
          } catch {
            toast.error("Payment verification failed. If money was deducted, it will be auto-refunded.");
          }
        },
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't start payment. Please try Cash on Delivery or WhatsApp.");
    } finally {
      setPlacing(false);
    }
  };

  const placeCod = async () => {
    if (!validate()) return;
    setPlacing(true);
    try {
      const { data } = await axios.post(`${API}/orders/cod`, orderPayload());
      meta.purchase(cartParams(items, payable), data.order_id);
      clear();
      setPlaced({ id: data.order_id, mode: "cod" });
      toast.success("Order placed — pay cash when it arrives.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't place the order. Please try again.");
    } finally {
      setPlacing(false);
    }
  };

  const sendWhatsAppOrder = () => {
    if (!validate()) return;
    const lines = [
      "Hi PhudG! I'd like to place an order.",
      "",
      ...items.map((i) => `• ${i.name} (${i.size}) × ${i.qty} — ₹${i.price * i.qty}`),
      "",
      ...(quote?.bundle_discount > 0 ? [`Bundle savings: −₹${quote.bundle_discount}`] : []),
      ...(quote?.coupon_discount > 0 ? [`Coupon ${quote.coupon_code}: −₹${quote.coupon_discount}`] : []),
      `Total: ₹${payable} (free delivery)`,
      ...(quote?.prepaid_discount > 0 ? [`Tip: pay online instead and save an extra 5% — ₹${quote.prepaid_total}`] : []),
      "",
      `Name: ${form.name}`,
      `Phone: ${form.phone}`,
      `Email: ${form.email}`,
      `Delivery address: ${fullAddress()}`,
      "",
      "Payment: I'll confirm with you here on WhatsApp.",
    ];
    meta.lead({ value: Number(payable), currency: "INR" });
    window.open(waLink(lines.join("\n")), "_blank", "noopener,noreferrer");
    clear();
    setPlaced({ id: null, mode: "whatsapp" });
  };

  const submit = () => {
    if (method === "online") return payOnline();
    if (method === "cod") return placeCod();
    return sendWhatsAppOrder();
  };

  const submitLabel = placing
    ? "Please wait…"
    : method === "online"
      ? `Pay ₹${payable} Online`
      : method === "cod"
        ? `Place Order — Pay ₹${payable} on Delivery`
        : "Send Order on WhatsApp";

  const METHODS = [
    { key: "online", icon: CreditCard, title: "Pay Online — Save Extra 5%", desc: "UPI, cards, netbanking — secure Razorpay checkout", testid: "payment-method-online" },
    { key: "cod", icon: Banknote, title: "Cash on Delivery", desc: "Pay in cash when your oils arrive", testid: "payment-method-cod" },
    { key: "whatsapp", icon: MessageCircle, title: "Confirm on WhatsApp", desc: "Sends your full order + address to us in one message", testid: "payment-method-whatsapp" },
  ];

  const icFired = useRef(false);
  useEffect(() => {
    if (!icFired.current && items.length > 0 && !placed) {
      icFired.current = true;
      meta.initiateCheckout(cartParams(items, total));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  return (
    <main data-testid="cart-page">
      <Seo title="Your Cart | PhudG" noindex />
      <Header />
      <section className="min-h-screen bg-cream pt-32 pb-24">
        <div className="max-w-5xl mx-auto px-6">
          <h1 className="font-serif text-5xl md:text-6xl text-ink tracking-tighter mb-12">
            Your <span className="italic text-olive">cart.</span>
          </h1>

          {placed && (
            <div data-testid="order-success" className="rounded-3xl border border-olive/30 bg-olive/5 p-10 text-center">
              <CheckCircle2 size={40} className="mx-auto text-olive mb-4" />
              <h2 className="font-serif text-3xl text-ink mb-3">
                {placed.mode === "whatsapp" ? "Order ready on WhatsApp!" : "Order placed!"}
              </h2>
              <p className="text-sm font-light text-ink/60 max-w-md mx-auto mb-2">
                {placed.mode === "online" &&
                  "Payment received. We've emailed your order confirmation and will reach out shortly with delivery details."}
                {placed.mode === "cod" &&
                  "Pay in cash when your oils arrive. We've emailed your order confirmation and will reach out shortly with delivery details."}
                {placed.mode === "whatsapp" &&
                  "Your complete order with delivery address has opened in WhatsApp — just hit send in the chat and we'll confirm shortly."}
              </p>
              <p className="text-sm font-light text-ink/60 max-w-md mx-auto mb-2">
                Thank you for choosing PhudG — good cooking begins with good ingredients.
              </p>
              {placed.id && <p className="text-xs text-ink/40">Order ID: {placed.id}</p>}
              <Link to="/" data-testid="order-success-home-link" className="mt-6 inline-block rounded-full bg-ink px-8 py-3 text-sm text-cream hover:bg-gold hover:text-ink transition-colors duration-300">
                Continue Shopping
              </Link>
            </div>
          )}

          {!placed && items.length === 0 && (
            <div className="rounded-3xl border border-ink/10 p-14 text-center">
              <ShoppingCart size={28} className="mx-auto text-ink/20 mb-4" />
              <p data-testid="cart-empty" className="text-ink/40 font-light mb-6">Your cart is empty.</p>
              <Link to="/" data-testid="cart-shop-link" className="rounded-full bg-ink px-8 py-3 text-sm text-cream hover:bg-gold hover:text-ink transition-colors duration-300">
                Browse the Oils
              </Link>
            </div>
          )}

          {!placed && items.length > 0 && (
            <div className="grid md:grid-cols-12 gap-10">
              <div className="md:col-span-7 space-y-4">
                {items.map((i, idx) => (
                  <motion.div
                    key={`${i.slug}-${i.size}`}
                    data-testid={`cart-item-${idx}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: idx * 0.05 }}
                    className="flex items-center gap-5 rounded-3xl border border-ink/10 bg-white/70 p-5"
                  >
                    <img src={i.image} alt={i.name} className="w-16 h-20 object-cover rounded-2xl" />
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-lg text-ink leading-tight">{i.name}</p>
                      <p className="text-xs text-ink/50 mt-0.5">{i.size} · ₹{i.price} each</p>
                      <div className="flex items-center gap-3 mt-3">
                        <button data-testid={`cart-minus-${idx}`} onClick={() => setQty(i.slug, i.size, i.qty - 1)} className="h-7 w-7 rounded-full border border-ink/15 flex items-center justify-center text-ink/60 hover:border-gold" aria-label="Decrease quantity">
                          <Minus size={13} />
                        </button>
                        <span data-testid={`cart-qty-${idx}`} className="text-sm w-5 text-center">{i.qty}</span>
                        <button data-testid={`cart-plus-${idx}`} onClick={() => setQty(i.slug, i.size, i.qty + 1)} className="h-7 w-7 rounded-full border border-ink/15 flex items-center justify-center text-ink/60 hover:border-gold" aria-label="Increase quantity">
                          <Plus size={13} />
                        </button>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-serif text-xl text-ink">₹{i.price * i.qty}</p>
                      <button data-testid={`cart-remove-${idx}`} onClick={() => remove(i.slug, i.size)} className="mt-2 text-ink/30 hover:text-red-500 transition-colors" aria-label="Remove item">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="md:col-span-5">
                <div className="rounded-3xl border border-ink/10 bg-white/70 p-7 sticky top-24">
                  <p className="text-xs tracking-[0.3em] uppercase text-amberbrand mb-5">Delivery Details</p>
                  <div className="space-y-3 mb-6">
                    <input data-testid="checkout-name-input" value={form.name} onChange={set("name")} placeholder="Full name *" className={inputCls} />
                    <div className="grid grid-cols-2 gap-3">
                      <input data-testid="checkout-phone-input" value={form.phone} onChange={set("phone")} placeholder="Phone *" className={inputCls} />
                      <input data-testid="checkout-email-input" type="email" value={form.email} onChange={set("email")} placeholder="Email *" className={inputCls} />
                    </div>
                    <input data-testid="checkout-address1-input" value={form.address1} onChange={set("address1")} placeholder="House / flat, street *" className={inputCls} />
                    <div className="grid grid-cols-2 gap-3">
                      <input data-testid="checkout-landmark-input" value={form.landmark} onChange={set("landmark")} placeholder="Landmark (optional)" className={inputCls} />
                      <input data-testid="checkout-city-input" value={form.city} onChange={set("city")} placeholder="City *" className={inputCls} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <input data-testid="checkout-state-input" value={form.state} onChange={set("state")} placeholder="State *" className={inputCls} />
                      <input data-testid="checkout-pincode-input" inputMode="numeric" maxLength={6} value={form.pincode} onChange={set("pincode")} placeholder="PIN code *" className={inputCls} />
                    </div>
                  </div>

                  <p className="text-xs tracking-[0.3em] uppercase text-amberbrand mb-3">Payment Method</p>
                  <div className="space-y-2 mb-6" data-testid="payment-methods">
                    {METHODS.map((m) => {
                      const Icon = m.icon;
                      const active = method === m.key;
                      return (
                        <button
                          key={m.key}
                          type="button"
                          data-testid={m.testid}
                          onClick={() => setMethod(m.key)}
                          className={`w-full flex items-center gap-4 rounded-2xl border px-4 py-3.5 text-left transition-colors duration-300 ${
                            active ? "border-gold bg-gold/10" : "border-ink/10 bg-white hover:border-gold/50"
                          }`}
                        >
                          <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors duration-300 ${active ? "bg-gold text-ink" : "bg-ink/5 text-ink/50"}`}>
                            <Icon size={16} />
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm text-ink">{m.title}</span>
                            <span className="block text-xs font-light text-ink/45">{m.desc}</span>
                          </span>
                          <span className={`h-4 w-4 shrink-0 rounded-full border-2 transition-colors duration-300 ${active ? "border-gold bg-gold" : "border-ink/20"}`} />
                        </button>
                      );
                    })}
                  </div>

                  {litreCount === 1 && (
                    <p data-testid="bundle-hint" className="mb-4 rounded-2xl border border-gold/40 bg-gold/10 px-4 py-3 text-xs text-ink/70">
                      Add one more 1-litre bottle to unlock <strong>12% bundle savings</strong> — auto-applied.
                    </p>
                  )}
                  {(litreCount === 2 || litreCount === 3) && (
                    <p data-testid="bundle-hint" className="mb-4 rounded-2xl border border-olive/40 bg-olive/10 px-4 py-3 text-xs text-ink/70">
                      <strong>12% bundle savings applied.</strong> Reach 4 one-litre bottles for 15% off.
                    </p>
                  )}
                  {litreCount >= 4 && (
                    <p data-testid="bundle-hint" className="mb-4 rounded-2xl border border-olive/40 bg-olive/10 px-4 py-3 text-xs text-ink/70">
                      <strong>15% family-pack savings applied.</strong> Smart kitchen stocking.
                    </p>
                  )}

                  <p className="text-xs tracking-[0.3em] uppercase text-amberbrand mb-3">Have a code?</p>
                  <div className="flex gap-2 mb-2">
                    <input
                      data-testid="coupon-input"
                      value={coupon}
                      onChange={(e) => setCoupon(e.target.value)}
                      placeholder="FIRSTPHUDG"
                      className={`${inputCls} uppercase tracking-widest`}
                    />
                    <button
                      data-testid="coupon-apply-btn"
                      onClick={applyCoupon}
                      className="shrink-0 rounded-2xl border border-ink/15 px-5 text-xs tracking-[0.2em] uppercase text-ink/70 hover:border-gold hover:text-gold transition-colors duration-300"
                    >
                      Apply
                    </button>
                  </div>
                  {quote?.coupon_error && couponApplied && (
                    <p data-testid="coupon-error" className="text-xs text-red-500 mb-4">{quote.coupon_error}</p>
                  )}
                  {quote?.coupon_lapsed && (
                    <p data-testid="coupon-lapsed" className="text-xs text-amberbrand mb-4">
                      FIRSTPHUDG set aside — your bundle saving is higher than 10%, so we applied that instead.
                    </p>
                  )}
                  {quote?.coupon_discount > 0 && (
                    <p data-testid="coupon-success" className="text-xs text-olive mb-4">FIRSTPHUDG applied — 10% off your first order</p>
                  )}

                  <div className="space-y-2 border-t border-ink/10 pt-5 mb-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-ink/60">Subtotal</span>
                      <span className="text-sm text-ink/80">₹{quote?.subtotal ?? total}</span>
                    </div>
                    {quote?.bundle_discount > 0 && (
                      <div className="flex items-center justify-between" data-testid="bundle-discount-row">
                        <span className="text-sm text-olive">Bundle savings</span>
                        <span className="text-sm text-olive">−₹{quote.bundle_discount}</span>
                      </div>
                    )}
                    {quote?.coupon_discount > 0 && (
                      <div className="flex items-center justify-between" data-testid="coupon-discount-row">
                        <span className="text-sm text-olive">Coupon {quote.coupon_code}</span>
                        <span className="text-sm text-olive">−₹{quote.coupon_discount}</span>
                      </div>
                    )}
                    {method === "online" && quote?.prepaid_discount > 0 && (
                      <div className="flex items-center justify-between" data-testid="prepaid-discount-row">
                        <span className="text-sm text-olive">Prepaid savings (pay online)</span>
                        <span className="text-sm text-olive">−₹{quote.prepaid_discount}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-1">
                      <span className="text-sm text-ink/60">Total</span>
                      <span data-testid="cart-total" className="font-serif text-3xl text-ink">₹{payable}</span>
                    </div>
                  </div>
                  <p className="text-xs text-ink/40 mb-6">Free delivery — included in the price.</p>
                  <motion.button
                    data-testid="checkout-submit-btn"
                    onClick={submit}
                    disabled={placing}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                    className="w-full rounded-full bg-gold px-8 py-4 text-sm font-medium text-ink hover:bg-amberbrand hover:text-cream transition-colors duration-300 disabled:opacity-60"
                  >
                    {submitLabel}
                  </motion.button>
                  <p className="mt-4 flex items-center justify-center gap-2 text-[11px] text-ink/40">
                    <ShieldCheck size={13} className="text-olive" /> Secure payment by Razorpay · UPI, cards, netbanking
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </main>
  );
}
